/**
 * External dependencies
 */
import React, { memo, useMemo } from 'react';
import Avatar from '@mui/material/Avatar';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Collapse from '@mui/material/Collapse';
import useMaterialTheme from '@naruto/naruto-hooks/src/useMaterialTheme';
import Image from '@naruto/naruto-components/src/base/v1/Image';

/**
 * Internal dependencies
 */
import useLang from '@naruto/bgdcms/src/hooks/useLang';
import { loadImage } from '@naruto/bgdcms/src/tools/image-loader';
import useData, { LIST_CHILD_PADDING } from './useData';
import { SideItemProps } from './types';

function SideItem(props: SideItemProps): React.ReactElement {
  /* Global & Local State */
  const { item, parentRoute, childPadding, onChangeRoute } = props;
  const { i18ns } = useLang();
  const theme = useMaterialTheme();
  const { selected, parentSelected, renderChild, collapse, onToggleCollapse, onSelectRoute } = useData(props);

  /* Views */
  const selectImage = useMemo<React.ReactNode>(() => {
    if (item.icon && item.iconActive) {
      if (selected || parentSelected) {
        return (
          <ListItemAvatar>
            <Avatar>
              <Image src={loadImage(item.iconActive)} typeSize="small" />
            </Avatar>
          </ListItemAvatar>
        );
      }
      return (
        <ListItemAvatar>
          <Avatar>
            <Image src={loadImage(item.icon)} typeSize="small" />
          </Avatar>
        </ListItemAvatar>
      );
    }
    return <React.Fragment />;
  }, [item, selected, parentSelected]);

  /* Main */
  return (
    <React.Fragment>
      <ListItem
        style={{
          paddingLeft: childPadding ? `${childPadding}px` : '',
          backgroundColor: renderChild && collapse ? theme.palette.background.paper : '',
          color: parentSelected ? theme.palette.primary.main : ''
        }}
        button
        selected={selected || parentSelected}
        onClick={onSelectRoute}
      >
        {selectImage}
        <ListItemText primary={item.customName ? item.lang : i18ns.__(item.lang ? item.lang : '')} />
        {renderChild && collapse && (
          <ExpandLess fontSize="small" style={{ color: theme.palette.text.primary }} onClick={onToggleCollapse} />
        )}
        {renderChild && !collapse && (
          <ExpandMore fontSize="small" style={{ color: theme.palette.text.primary }} onClick={onToggleCollapse} />
        )}
      </ListItem>
      {renderChild && item.subRoute && (
        <Collapse in={collapse}>
          <List
            component="div"
            disablePadding
            style={{ backgroundColor: collapse ? theme.palette.background.paper : '' }}
          >
            {item.subRoute.map(
              (ele, index) =>
                ele.showInSidebar && (
                  <SideItem
                    key={index}
                    parentRoute={`${parentRoute}${item.route}`}
                    childPadding={childPadding ? childPadding + 24 : LIST_CHILD_PADDING}
                    item={ele}
                    onChangeRoute={onChangeRoute}
                  />
                )
            )}
          </List>
        </Collapse>
      )}
    </React.Fragment>
  );
}

export default memo(SideItem);
